import { Country } from '@superbet-group/web.types';
import {
    GroupAllowance,
    GroupConfig,
    MarketAllowance,
    SportConfig,
} from '../types';
import {
    createAllowancesKey,
    mergeToExistingAllowances,
} from '../utils';

export function getGroupMarketAllowances(
    marketAllowances: MarketAllowance[] | undefined,
    sportLevelEnableInPlay: boolean | undefined,
    target: Country,
): MarketAllowance[] {
    return marketAllowances
        ?.filter((allowance) => {
            return !allowance.omitInTargets || !allowance.omitInTargets.includes(target);
        }).map((allowance) => {
            return {
                ...allowance,
                enableInplay: sportLevelEnableInPlay ?? allowance.enableInplay,
            };
        }) ?? [];
}

export function getGroupMarketAllowancesFromOtherGroups(
    groupAllowances: GroupAllowance[] | null,
    groupConfig: GroupConfig[],
    target: Country,
): number[] {
    if (!groupAllowances) {
        return [];
    }
    const marketIds: number[] = [];
    const groups: Record<string, GroupConfig> = {};
    groupConfig.forEach((group) => {
        groups[group.group] = group;
    });
    groupAllowances.forEach((groupAllowance) => {
        if (!groupAllowance.omitInTargets || !groupAllowance.omitInTargets.includes(target)) {
            const group = groups[groupAllowance.group];
            if (group) {
                group.marketIds.forEach((marketId) => {
                    marketIds.push(marketId);
                });
            }
        }
    });
    return marketIds;
}

export function getAllowancesFromGroupConfig(
    currentGroupMarketAllowances: MarketAllowance[],
    marketAllowanceIdsFromOtherGroups: number[],
    groupMarketIds: number[],
): Record<string, Set<MarketAllowance>> {
    const marketsCombinableWithGroupMarkets = [
        ...currentGroupMarketAllowances.map((market) => market.marketId),
        ...marketAllowanceIdsFromOtherGroups,
    ];

    const pairs: Record<string, Set<MarketAllowance>> = {};
    groupMarketIds.forEach((marketAId) => {
        marketsCombinableWithGroupMarkets.forEach((marketBId) => {
            const combinationAllowances = currentGroupMarketAllowances.filter((allowance) => {
                return allowance.marketId === marketAId || allowance.marketId === marketBId;
            });
            const key = createAllowancesKey(marketAId, marketBId);
            pairs[key] = mergeToExistingAllowances(pairs, combinationAllowances, key);
        });
    });

    return pairs;
}

export function mapMarketPairsToMarketAllowances(
    config: Record<string, SportConfig>,
    target: Country,
): Record<string, Set<MarketAllowance>> {
    const pairs: Record<string, Set<MarketAllowance>> = {};
    Object.entries(config).forEach(([_sport, sportConfig]) => {
        sportConfig.groups.forEach((group) => {
            const groupMarketAllowancesFromOtherGroups = getGroupMarketAllowancesFromOtherGroups(
                group.groupAllowances,
                sportConfig.groups,
                target,
            );
            const currentGroupMarketAllowances = getGroupMarketAllowances(
                group.marketAllowances,
                sportConfig.enableInplay,
                target,
            );
            const allowances = getAllowancesFromGroupConfig(
                currentGroupMarketAllowances,
                groupMarketAllowancesFromOtherGroups,
                group.marketIds,
            );

            Object.entries(allowances).forEach(([key, value]) => {
                pairs[key] = mergeToExistingAllowances(pairs, Array.from(value), key);
            });
        });
        sportConfig.markets.forEach((market) => {
            const marketAllowances = getGroupMarketAllowances(
                market.marketAllowances,
                sportConfig.enableInplay,
                target,
            );
            marketAllowances.forEach((allowance) => {
                const key = createAllowancesKey(market.marketId, allowance.marketId);
                pairs[key] = mergeToExistingAllowances(pairs, marketAllowances, key);
            });
        });
    });
    return pairs;
}
